import { useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import axiosInstance from "@/api/config";
import NotificationContent from "@/components/notification/notificationContent";
import { ALL_COLORS, COLORS } from "@/constants/colors";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import { MESSAGE } from "@/constants/message";
import { NPS_MILESTONES } from "@/constants/nps";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { RootState } from "@/store";
import { removeUnOpenedJobId } from "@/store/slice/analysisJob";
import {
  resetGridsSelected,
  setIsHasSummary
} from "@/store/slice/analysisSummary";
import { addDataset, addRawDataset } from "@/store/slice/datasetSlice";
import { clearCurrRunningAnalysisId } from "@/store/slice/spatialSlice";
import {
  setNpsMilestoneReached,
  setUIDataConfig,
  uiInitialState
} from "@/store/slice/uiSlice";
import { clearWidgets } from "@/store/slice/widgetSlice";
import {
  AnalysisStatus,
  Dataset,
  DatasetChunkStream,
  DatasetData,
  DatasetDataRowV2,
  DatasetMetadataStream,
  DatasetStreamDone,
  EColorNames
} from "@/types/dataset";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import { computeStepValue } from "@/utils/stepValue";

import { useConfiguration } from "../project/useConfiguration";
import useToast, { EToastType } from "../ui/useToast";
import { useAppFeatures } from "../useAppFeatures";
import useEventSource from "../useEventSource";

type AnalysisStream = {
  analysis_status: { id: string; status: AnalysisStatus; message?: string };
  "analysis_result:meta": DatasetMetadataStream[];
  "analysis_result:chunk": DatasetChunkStream;
  "analysis_result:done": DatasetStreamDone;
  "dataset_data:chunk": DatasetChunkStream;
  "dataset_data:done": DatasetStreamDone;
  "stream:done": { dataset_id: string };
  "stream:error": void;
};

export const useAnalysisResult = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const chunkBufferRef = useRef<DatasetDataRowV2[]>([]);
  const datasetMetadataRef = useRef<{
    [id: string]: DatasetMetadataStream;
  }>({});
  const datasetDataRef = useRef<{
    [id: string]: { datasetData: DatasetData; newDataset: Dataset };
  }>({});
  const analysisStatusRef = useRef<AnalysisStatus>();
  const analysisMessageRef = useRef<string>();
  const hasHandledAnalysisErrorRef = useRef(false);

  const currRunningAnalysisId = useSelector(
    (state: RootState) => state.spatial.currRunningAnalysisId
  );
  const analysisResultOpenSource = useSelector(
    (state: RootState) => state.spatial.analysisResultOpenSource
  );

  const { deleteAllFeatures, map } = useMap();

  const {
    handleClearDataset,
    generateDatasetData,
    handleAddSpatialAnalysisDatasetByType
  } = useDataset();
  const { mutateGetAnalysisConfig } = useConfiguration({});
  const { isDatasetWidgetEnabled } = useAppFeatures();

  const handleAnalysisError = () => {
    if (hasHandledAnalysisErrorRef.current) return;

    hasHandledAnalysisErrorRef.current = true;
    dispatch(clearCurrRunningAnalysisId());
    toast.dismiss("spatial-analysis-toast");

    const errorMessage = analysisMessageRef.current || "";
    const [errorTitle, ...errorDescParts] = errorMessage.split("-");
    const errorDescription = errorDescParts.join("-").trim();
    analysisMessageRef.current = undefined;

    showToast({
      type: EToastType.ERROR,
      message: (
        <NotificationContent
          title={errorTitle?.trim() || "Something Went Wrong"}
          description={
            errorDescription ||
            "We’re sorry, we cannot proceed with your request. Please try again."
          }
        />
      )
    });
    analysisStatusRef.current = undefined;
  };

  useEventSource<AnalysisStream>({
    url: `${axiosInstance.defaults.baseURL}/v1/analysis/${currRunningAnalysisId}/stream`,
    eventKey: ["analysis", { id: currRunningAnalysisId }],
    parse: (data) => JSON.parse(data),
    onOpen: () => {
      hasHandledAnalysisErrorRef.current = false;
      // Entering the analysis-result stream: clear any input geometry (drawn
      // polygons) still on the map so it isn't visible while the result loads.
      // stream:done also clears, but only at the end — this covers the gap.
      deleteAllFeatures();
      dispatch(resetGridsSelected());
      showToast({
        id: "spatial-analysis-toast",
        type: EToastType.LOADING,
        message: (
          <NotificationContent
            title={MESSAGE.LOADING_SPATIAL_ANALYSIS}
            description={MESSAGE.PREPARE_TABULAR_DATA_AND_MAPVIEW}
          />
        )
      });
    },
    eventHandlers: {
      analysis_status: (data) => {
        analysisStatusRef.current = data.status;
        if (data.message) {
          analysisMessageRef.current = data.message;
        }

        if (data.status === AnalysisStatus.FAILED) {
          handleAnalysisError();
        }
      },
      "analysis_result:meta": (data) => {
        data.forEach((dataset) => {
          datasetMetadataRef.current[dataset.dataset_id] = dataset;
        });
      },
      "analysis_result:chunk": (data) => {
        // Avoid spread: `push(...rows)` throws RangeError once a large chunk
        // exceeds the engine's argument limit; useEventSource swallows it.
        for (const row of data.rows) chunkBufferRef.current.push(row);
      },
      "analysis_result:done": (data: DatasetStreamDone) => {
        const dataset = {
          dataset_id: data.dataset_id,
          rows: chunkBufferRef.current,
          seq: data.final_seq
        };

        const { datasetData, newDataset } = generateDatasetData(
          dataset,
          datasetMetadataRef.current[data.dataset_id],
          true
        );

        datasetDataRef.current[data.dataset_id] = {
          datasetData,
          newDataset
        };

        chunkBufferRef.current = [];
      },
      "dataset_data:chunk": (data) => {
        // Avoid spread: `push(...rows)` throws RangeError once a large chunk
        // exceeds the engine's argument limit; useEventSource swallows it.
        for (const row of data.rows) chunkBufferRef.current.push(row);
      },
      "dataset_data:done": (data) => {
        const dataset = {
          dataset_id: data.dataset_id,
          rows: chunkBufferRef.current,
          seq: data.final_seq
        };

        const { datasetData, newDataset } = generateDatasetData(
          dataset,
          datasetMetadataRef.current[data.dataset_id],
          true
        );

        datasetDataRef.current[data.dataset_id] = {
          datasetData,
          newDataset
        };

        chunkBufferRef.current = [];
      },
      "stream:done": () => {
        if (analysisStatusRef.current !== AnalysisStatus.SUCCESS) {
          dispatch(clearCurrRunningAnalysisId());
          datasetDataRef.current = {};
          analysisStatusRef.current = undefined;
          analysisMessageRef.current = undefined;

          return;
        }

        // Snapshot before any dispatch that clears this value — used by both
        // mutateGetAnalysisConfig and the fire-and-forget widget fetch below.
        const snapshotAnalysisId = currRunningAnalysisId;

        if (snapshotAnalysisId) {
          const shouldFetchAnalysisConfig =
            analysisResultOpenSource === "analysis-job-list";

          // Clear current running analysis ID first to immediately disable stream re-subscription
          // (prevents new EventSource connection from opening while waiting for slow config fetch)
          dispatch(clearCurrRunningAnalysisId());

          if (shouldFetchAnalysisConfig) {
            // Then fetch analysis configuration using the previously active analysis ID.
            // The mutation owns user-facing error handling; this catch only prevents
            // an unhandled rejection because the refresh is intentionally fire-and-forget.
            void mutateGetAnalysisConfig({
              analysisId: snapshotAnalysisId
            });
          }
        }

        const finalMessage = analysisMessageRef.current || "";
        const [rawTitle, ...descParts] = finalMessage.split("-");
        const title = rawTitle?.trim() || "";
        const description = descParts.join("-").trim();

        const isHasNoScore = [
          MESSAGE.SPATIAL_ANALYSIS_NO_DATA_AVAILABLE,
          MESSAGE.SPATIAL_ANALYSIS_SCORE_GENERATION_FAILED
        ].includes(title);

        deleteAllFeatures();
        handleClearDataset();
        dispatch(
          setUIDataConfig({
            ...uiInitialState,
            showSpatialAnalysisResultTabularModal: false,
            showComparisonPopup: {
              isShow: false,
              isFromAnalysisResult: false,
              isLimitShow: false
            },
            sideBarMenuTypePopupDisplay: null,
            showCatchmentPopup: false,
            showAnalysisResultPopup: true,
            showAnalysisSummaryPopup: isDatasetWidgetEnabled,
            sidebarUserSettingPopupDisplay: {
              isPanelView: false,
              isPanelAdd: false,
              isPanelEdit: false
            }
          })
        );
        toast.dismiss("spatial-analysis-toast");
        dispatch(removeUnOpenedJobId(snapshotAnalysisId ?? ""));
        if (isDatasetWidgetEnabled) {
          dispatch(clearWidgets());
        }

        const entries = Object.entries(datasetDataRef.current);
        const isAnalysisResultEntry = ([, data]: (typeof entries)[number]) =>
          data.datasetData.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME;

        // The "spatial analysis result" dataset must be processed last so its
        // layer renders on top of the others. Keep the remaining datasets
        // reversed (newest first) and append the analysis result at the end.
        const datasetDataList = [
          ...entries.filter((entry) => !isAnalysisResultEntry(entry)).reverse(),
          ...entries.filter(isAnalysisResultEntry)
        ];

        for (const [index, [datasetId, data]] of datasetDataList.entries()) {
          const { datasetData, newDataset } = data;
          const featureCollection =
            convertDatasetResponseToFeatureCollection(datasetData);
          dispatch(addRawDataset({ dataset: datasetData, id: datasetId }));
          dispatch(addDataset({ dataset: newDataset }));

          // Loop through colors if there are more datasets than available colors
          const loopIndex = index % ALL_COLORS.length;
          const colorName = ALL_COLORS[loopIndex] as keyof typeof EColorNames;

          handleAddSpatialAnalysisDatasetByType({
            spatialType: datasetData.spatial_aggregation_type,
            newDataset: {
              ...newDataset,
              style: {
                ...newDataset.style,
                fillColor: COLORS[colorName][500],
                colorFamily: EColorNames[colorName],
                colorSteps: computeStepValue(datasetData.rows.length)
              }
            },
            featureCollection
          });
        }

        dispatch(setIsHasSummary(!isHasNoScore));
        dispatch(
          setNpsMilestoneReached(NPS_MILESTONES.COMPLETED_FIRST_ANALYSIS)
        );

        datasetDataRef.current = {};
        analysisStatusRef.current = undefined;

        analysisMessageRef.current = undefined;

        showToast({
          type: isHasNoScore ? EToastType.WARNING : EToastType.SUCCESS,
          message: (
            <NotificationContent
              title={
                title
                  ? MESSAGE.SPATIAL_ANALYSIS_SCORE_NOT_AVAILABLE_TITLE
                  : MESSAGE.SPATIAL_ANALYSIS_SUCCESS_TITLE
              }
              description={
                description
                  ? MESSAGE.SPATIAL_ANALYSIS_SCORE_NOT_AVAILABLE_DESCRIPTION
                  : MESSAGE.SPATIAL_ANALYSIS_SUCCESS_DESCRIPTION
              }
            />
          )
        });
      },
      "stream:error": handleAnalysisError
    },
    enabled: !!currRunningAnalysisId && map?._loaded
  });
};

export default useAnalysisResult;
